import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { Icon, type IconName } from './icons';
import { focusRing, Pressable, Text } from './primitives';

/**
 * A table's filter controls, from the design system's `TableToolbar`, `FilterChip` and the
 * Filters side panel (Seller Dashboard Shell, S08).
 */

/** A chip's look, shared by the panel chips and the toggle chips: accent-tinted while it is on. */
function chipStyle(on: boolean, hovered: boolean, focused: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: cssVar('space.xs'),
    flexShrink: 0,
    minHeight: cssVar('size.target.min'),
    padding: `0 ${cssVar('space.md')}`,
    boxSizing: 'border-box',
    borderRadius: cssVar('radius.sm'),
    border: `1px solid ${cssVar(on ? 'color.accent.default' : hovered ? 'color.border.strong' : 'color.border.default')}`,
    background: cssVar(on ? 'color.accent.subtle' : 'color.bg.raised'),
    color: cssVar(on ? 'color.accent.default' : 'color.fg.default'),
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    fontFamily: cssVar('family.base'),
    fontSize: cssVar('type.label.size'),
    fontWeight: cssVar('type.title.weight'),
    lineHeight: cssVar('type.label.line'),
    ...(focused ? focusRing : {}),
  } as const;
}

/**
 * One chip in a table's toolbar. Every chip opens the filter panel (`aria-haspopup`):
 * `Filters` itself with its icon and how many filters are on, and one chip per filter
 * showing the value it holds, accent-tinted while it holds one.
 */
export function FilterChip({
  id,
  controls,
  label,
  value,
  count,
  icon,
  title,
  expanded,
  onPress,
}: {
  id?: string;
  /** The panel's id, while it is open: what this chip shows and hides. */
  controls?: string | undefined;
  label: string;
  /** What the filter is set to, shortened for the chip; nothing means "any". */
  value?: string | undefined;
  /** For `Filters`: how many filters are on. */
  count?: number | undefined;
  icon?: IconName;
  /** The full value, where `value` is shortened. */
  title?: string | undefined;
  /** Only the chip that opened the panel says it is expanded. */
  expanded: boolean;
  onPress: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const on = value !== undefined || (count !== undefined && count > 0);
  const shown = value ?? (count ? String(count) : undefined);
  return (
    <button
      id={id}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls={controls}
      // The chip's text is the label and the short value; the name carries the whole value.
      aria-label={value !== undefined ? `${label}: ${title ?? value}` : count ? `${label}, ${count} on` : label}
      title={title}
      onClick={onPress}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={chipStyle(on, hovered, focused)}
    >
      {icon ? <Icon name={icon} /> : null}
      <span>
        {label}
        {shown !== undefined ? ` · ${shown}` : null}
      </span>
      {icon ? null : <Icon name="chevron-down" />}
    </button>
  );
}

/**
 * The Filters side panel: fixed to the right, under the top bar rather than over it, with
 * no scrim, so the table stays readable beside it. A non-modal dialog: it takes focus when
 * it opens, hands it back to whatever opened it when it closes, and Escape (from inside
 * it) or the close button dismisses it.
 *
 * Its body and footer are one form, so Enter in a field does what the footer's submit
 * button does.
 */
export function SidePanel({
  id,
  returnFocusTo,
  title,
  open,
  onClose,
  initialFocus,
  formLabel,
  onSubmit,
  children,
  footer,
}: {
  id: string;
  /** Where focus goes on close when nothing outside the panel had it on open (the Filters chip). */
  returnFocusTo: string;
  title: string;
  formLabel: string;
  onSubmit: (event: FormEvent) => void;
  open: boolean;
  onClose: () => void;
  /** The control to focus on open; the close button when absent. */
  initialFocus?: string | undefined;
  children: ReactNode;
  footer: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [closeFocused, setCloseFocused] = useState(false);
  const titleId = useId();

  const opener = useRef<HTMLElement | null>(null);

  // The opener, captured once as the panel opens (before focus moves into it), gets focus
  // back on close. Focus on the page itself, or an opener since removed, falls back.
  useEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    return () => {
      const back = opener.current?.isConnected ? opener.current : document.getElementById(returnFocusTo);
      back?.focus();
    };
  }, [open, returnFocusTo]);

  // Into the panel on open, and to the new field when another chip is pressed while open.
  useEffect(() => {
    if (!open) return;
    const target = (initialFocus && document.getElementById(initialFocus)) || closeRef.current;
    target?.focus();
  }, [open, initialFocus]);

  if (!open) return null;
  const edge = `1px solid ${cssVar('color.border.default')}`;
  return (
    <aside
      id={id}
      role="dialog"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
      style={{
        position: 'fixed',
        top: cssVar('size.bar.top'),
        right: 0,
        bottom: 0,
        zIndex: 20,
        width: cssVar('size.panel.side'),
        maxWidth: '100vw',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        background: cssVar('color.bg.sheet'),
        borderLeft: edge,
        boxShadow: cssVar('elevation.overlay'),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: cssVar('space.md'),
          padding: `${cssVar('space.md')} ${cssVar('space.md')} ${cssVar('space.md')} ${cssVar('space.lg')}`,
          borderBottom: edge,
        }}
      >
        <Text as="h2" step="heading">
          <span id={titleId}>{title}</span>
        </Text>
        <button
          ref={closeRef}
          type="button"
          aria-label={`Close ${title.toLowerCase()}`}
          onClick={onClose}
          onFocus={(event) => setCloseFocused(event.currentTarget.matches(':focus-visible'))}
          onBlur={() => setCloseFocused(false)}
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: cssVar('size.target.min'),
            height: cssVar('size.target.min'),
            border: 'none',
            borderRadius: cssVar('radius.md'),
            background: 'transparent',
            color: cssVar('color.fg.muted'),
            cursor: 'pointer',
            ...(closeFocused ? focusRing : {}),
          }}
        >
          <Icon name="x" />
        </button>
      </div>
      <form
        aria-label={formLabel}
        noValidate
        onSubmit={onSubmit}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: cssVar('space.xl'),
            padding: cssVar('space.lg'),
          }}
        >
          {children}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: cssVar('space.sm'),
            padding: `${cssVar('space.md')} ${cssVar('space.lg')}`,
            borderTop: edge,
          }}
        >
          {footer}
        </div>
      </form>
    </aside>
  );
}

/**
 * The next value of a single-select set of toggle chips when one is pressed: that chip,
 * or — pressing the one already on — none. How the design's status chips work: there is no
 * "All" chip, because pressing the selected one again *is* "All".
 */
export function toggledChip<Value extends string>(current: Value | undefined, pressed: Value): Value | undefined {
  return current === pressed ? undefined : pressed;
}

/**
 * A chip that is on or off (the design system's `FilterChip` with `selected`): a pressed
 * button, with a check while it is on so the state is not carried by color alone.
 */
export function ToggleChip({ label, on, onPress }: { label: string; on: boolean; onPress: () => void }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPress}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={chipStyle(on, hovered, focused)}
    >
      {on ? <Icon name="check" /> : null}
      <span>{label}</span>
    </button>
  );
}

export interface ChipChoice<Value extends string> {
  value: Value;
  label: string;
}

/**
 * Above a table (the design system's `TableToolbar`): a search box with a clear button,
 * single-select toggle chips, the live count of what is shown, and Export.
 *
 * **Export is the design system's default and has to be turned off by saying so**
 * (`exportable={false}`, as the Sellers page does); turned on, it needs its handler, so
 * there is no Export button that does nothing.
 */
export function TableToolbar<Value extends string>(
  props: {
    search: string;
    onSearch: (term: string) => void;
    /** Enter in the search box; for a search the server runs rather than one that filters as it types. */
    onSubmit?: (term: string) => void;
    /** The placeholder, which is also the search box's name. */
    placeholder: string;
    chips?: {
      choices: readonly ChipChoice<Value>[];
      value: Value | undefined;
      onChange: (value: Value | undefined) => void;
    };
    /** "8 sellers": announced politely as it changes. */
    count?: string | undefined;
    /** A line under the search box, read with it: how it matches ("starts with"), or how short a term it takes. */
    hint?: string | undefined;
  } & ({ exportable: false } | { exportable?: true; onExport: () => void; exporting?: boolean }),
) {
  const { search, onSearch, onSubmit, placeholder, chips, count, hint } = props;
  const input = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [focused, setFocused] = useState(false);
  const [clearFocused, setClearFocused] = useState(false);
  return (
    // `search` is the landmark; the form inside it is what makes Enter submit.
    <search>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.(search);
        }}
        // Top-aligned, so a hint under the search box does not push the chips off its line.
        style={{ display: 'flex', alignItems: 'flex-start', gap: cssVar('space.md'), flexWrap: 'wrap' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: cssVar('space.xs'),
            width: cssVar('size.search'),
            maxWidth: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: cssVar('space.sm'),
              boxSizing: 'border-box',
              width: '100%',
              minHeight: cssVar('size.target.min'),
              padding: `0 ${cssVar('space.xs')} 0 ${cssVar('space.md')}`,
              borderRadius: cssVar('radius.md'),
              background: cssVar('color.bg.base'),
              border: `1px solid ${cssVar('color.border.default')}`,
              color: cssVar('color.fg.muted'),
              // The ring goes on the box, not the input inside it, so it rounds the whole field.
              ...(focused ? focusRing : {}),
            }}
          >
            <Icon name="search" />
            {/* biome-ignore lint/a11y/useSemanticElements: `type="search"` draws the second clear button this avoids. */}
            <input
              ref={input}
              // Text, not `search`: a search input draws the browser's own clear button beside
              // ours. The role keeps what `search` told a screen reader.
              type="text"
              role="searchbox"
              enterKeyHint="search"
              aria-label={placeholder}
              aria-describedby={hint ? hintId : undefined}
              placeholder={placeholder}
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: cssVar('color.fg.default'),
                fontFamily: cssVar('family.base'),
                fontSize: cssVar('type.body.size'),
              }}
            />
            {search !== '' ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  onSearch('');
                  input.current?.focus();
                }}
                onFocus={(event) => setClearFocused(event.currentTarget.matches(':focus-visible'))}
                onBlur={() => setClearFocused(false)}
                style={{
                  display: 'inline-grid',
                  placeItems: 'center',
                  width: cssVar('size.icon.button'),
                  height: cssVar('size.icon.button'),
                  padding: 0,
                  border: 'none',
                  borderRadius: cssVar('radius.md'),
                  background: 'transparent',
                  color: cssVar('color.fg.muted'),
                  cursor: 'pointer',
                  ...(clearFocused ? focusRing : {}),
                }}
              >
                <Icon name="x" />
              </button>
            ) : null}
          </div>
          {hint ? (
            <span id={hintId}>
              <Text step="label" tone="fg.muted">
                {hint}
              </Text>
            </span>
          ) : null}
        </div>
        {chips ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: cssVar('space.sm'),
              flex: 1,
              minWidth: 0,
              overflowX: 'auto',
            }}
          >
            {chips.choices.map((choice) => (
              <ToggleChip
                key={choice.value}
                label={choice.label}
                on={chips.value === choice.value}
                onPress={() => chips.onChange(toggledChip(chips.value, choice.value))}
              />
            ))}
          </div>
        ) : (
          <span style={{ flex: 1 }} />
        )}
        {/* `output` is a status region already: the count is announced politely as it changes. */}
        <output
          aria-live="polite"
          // Centred on the search box's line, the way the chips are.
          style={{ display: 'flex', alignItems: 'center', minHeight: cssVar('size.target.min') }}
        >
          {count !== undefined ? (
            <Text step="label" tone="fg.muted">
              {count}
            </Text>
          ) : null}
        </output>
        {props.exportable === false ? null : (
          <Pressable variant="quiet" onPress={props.onExport} busy={props.exporting === true}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: cssVar('space.xs') }}>
              <Icon name="download" />
              Export
            </span>
          </Pressable>
        )}
      </form>
    </search>
  );
}
